/**
 * Provider-neutral owner authentication. The Pinia auth store and the rest of the app depend
 * only on these types; `cognito.ts` implements them with Amplify, and `local.ts` with the
 * development server's fake sign-in.
 */

export interface AuthUser {
  /** Stable, opaque owner identifier (the Cognito `sub`). */
  id: string
  email: string
}

/** Outcome of a password sign-in. A challenge is not a session. */
export type SignInResult =
  | { step: 'done'; user: AuthUser }
  /** Invitation: the temporary password worked and the owner must choose their own. */
  | { step: 'new_password_required' }
  /** The operator required a reset; continue with the emailed-code flow. */
  | { step: 'reset_required' }

/** Session changes the provider observed, including from other tabs. */
export type AuthEvent =
  | { type: 'signed_in'; user: AuthUser }
  | { type: 'signed_out' }
  | { type: 'token_refreshed' }
  /** The refresh token was rejected: the session is over. */
  | { type: 'session_expired' }

export type AuthErrorCode =
  | 'invalid_credentials'
  | 'temporary_password_expired'
  | 'too_many_attempts'
  | 'password_policy'
  | 'code_mismatch'
  | 'code_expired'
  | 'no_challenge'
  | 'session_expired'
  | 'network'
  | 'not_configured'
  | 'unsupported'
  | 'unknown'

/** Every provider failure, so pages can explain each case without provider knowledge. */
export class AuthError extends Error {
  readonly code: AuthErrorCode

  constructor(code: AuthErrorCode, message: string, options?: { cause?: unknown }) {
    super(message, options)
    this.name = 'AuthError'
    this.code = code
  }
}

export interface AuthProvider {
  /** Signed-in user from stored tokens, refreshing them if needed; null when signed out. */
  restore(): Promise<AuthUser | null>
  signIn(email: string, password: string): Promise<SignInResult>
  /** Completes the invitation challenge from the preceding `signIn` in this page load. */
  completeNewPassword(password: string): Promise<AuthUser>
  /** A current access token (refreshed when near expiry), or null when the session is over. */
  accessToken(options?: { forceRefresh?: boolean }): Promise<string | null>
  signOut(): Promise<void>
  /** Emails a reset code. Unknown addresses are indistinguishable from known ones. */
  requestPasswordReset(email: string): Promise<void>
  confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void>
  changePassword(currentPassword: string, newPassword: string): Promise<void>
  /** False when tokens live only in memory, so a reload signs the owner out. */
  readonly persistent: boolean
  subscribe(listener: (event: AuthEvent) => void): () => void
}
